import React, { useState, useEffect } from 'react';
import '../styles/LayerControls.css';
import { TOPIC_GROUPS, DEFAULT_ON_LAYERS } from '../config/layers.js';
import { useTranslation } from '../translations';

const MAP_STYLES = [
  { id: 'light',     label: 'Light',    uri: 'mapbox://styles/mapbox/light-v11' },
  { id: 'dark',      label: 'Dark',     uri: 'mapbox://styles/mapbox/dark-v11' },
  { id: 'satellite', label: 'Satellite',uri: 'mapbox://styles/mapbox/satellite-streets-v12' },
  { id: 'terrain',   label: 'Terrain',  uri: 'mapbox://styles/mapbox/outdoors-v12' },
];

// Shorten the layer description into a one-liner so rows have consistent height.
function _shortDesc(desc) {
  if (!desc) return '';
  const firstSentence = desc.split(/[.——]/)[0].trim();
  return firstSentence.length > 80 ? firstSentence.slice(0, 77) + '…' : firstSentence;
}

// Is this layer relevant given the current country + use-case filters?
function _isLayerInScope(layer, selectedCountry, useCase) {
  if (layer.countries && !layer.countries.includes(selectedCountry)) return false;
  if (layer.useCases && !layer.useCases.includes(useCase)) return false;
  return true;
}

// Layer ids whose endpoints are known-dead; strip them from any persisted
// state so stale localStorage toggles don't keep spamming the console with
// 404s after a layer is retired. Append to this set — don't remove — until
// a working replacement endpoint lands.
const _RETIRED_LAYER_IDS = new Set([
  'cultural_heritage',  // Riksantikvaren Kulturminner WMS (kart.ra.no) — 404 since 2026-10
]);

// Load the persisted WMS/tiles/geojson on/off state from localStorage, merged
// with defaults. Only non-data (`kind !== 'data'`) layers live here — data
// layers are driven by parent state.
function _loadToggleState() {
  let stored = {};
  try { stored = JSON.parse(localStorage.getItem('seidr_layer_state') || '{}'); } catch {}
  for (const id of _RETIRED_LAYER_IDS) delete stored[id];
  const result = {};
  for (const group of TOPIC_GROUPS) {
    for (const layer of group.layers) {
      if (layer.kind === 'data') continue;
      result[layer.id] = (layer.id in stored)
        ? stored[layer.id]
        : DEFAULT_ON_LAYERS.includes(layer.id);
    }
  }
  return result;
}

const LayerControls = ({
  layers,
  onToggleLayer,
  currentStyle,
  onStyleChange,
  onWmsLayerChange,
  language = 'en',
  selectedCountry = 'NO',
  useCase = 'hydrogen',
}) => {
  const t = useTranslation(language);
  const [expanded, setExpanded] = useState(false);
  const [wmsLayers, setWmsLayers] = useState(() => _loadToggleState());
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    try { localStorage.setItem('seidr_layer_state', JSON.stringify(wmsLayers)); } catch {}
    if (onWmsLayerChange) onWmsLayerChange(wmsLayers);
  }, [wmsLayers]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleWmsLayer = (id) => {
    setWmsLayers(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Groups scoped to the current country + use-case (with any fully-empty
  // groups removed — e.g. "Geology" disappears if the user flips to FI).
  const visibleGroups = TOPIC_GROUPS
    .map(g => ({ ...g, layers: g.layers.filter(l => _isLayerInScope(l, selectedCountry, useCase)) }))
    .filter(g => g.layers.length > 0);

  const allVisibleLayers = visibleGroups.flatMap(g => g.layers);

  const showAll = () => {
    const next = { ...wmsLayers };
    for (const l of allVisibleLayers) {
      if (l.kind !== 'data') next[l.id] = true;
    }
    setWmsLayers(next);
    for (const l of allVisibleLayers) {
      if (l.kind === 'data' && layers[l.dataKey] && !layers[l.dataKey].visible) {
        onToggleLayer(l.dataKey);
      }
    }
  };

  const hideAll = () => {
    const next = { ...wmsLayers };
    for (const l of allVisibleLayers) {
      if (l.kind !== 'data') next[l.id] = false;
    }
    setWmsLayers(next);
    for (const l of allVisibleLayers) {
      if (l.kind === 'data' && layers[l.dataKey] && layers[l.dataKey].visible) {
        onToggleLayer(l.dataKey);
      }
    }
  };

  const toggleGroup = (id) => {
    setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const isLayerOn = (layer) => {
    if (layer.kind === 'data') return !!(layers[layer.dataKey] && layers[layer.dataKey].visible);
    return !!wmsLayers[layer.id];
  };

  const handleLayerToggle = (layer) => {
    if (layer.kind === 'data') onToggleLayer(layer.dataKey);
    else toggleWmsLayer(layer.id);
  };

  // Counts across every layer (data + wms/tiles/geojson) that is in-scope.
  const visibleCount = allVisibleLayers.filter(isLayerOn).length;
  const totalCount   = allVisibleLayers.length;

  return (
    <div className={`layer-toggle-panel ${expanded ? 'expanded' : ''}`}>
      <button className="layer-toggle-header" onClick={() => setExpanded(e => !e)}>
        <span className="layer-toggle-title">{t('mapControls.layers')}</span>
        <span className="layer-toggle-count">{visibleCount}/{totalCount}</span>
        <span className="layer-toggle-chevron">{expanded ? '▼' : '▲'}</span>
      </button>

      {expanded && (
        <div className="layer-toggle-list">
          <div className="layer-group-label">{t('mapControls.mapStyle')}</div>
          <div className="style-btn-group">
            {MAP_STYLES.map(s => (
              <button
                key={s.id}
                className={`style-pill ${currentStyle === s.uri ? 'active' : ''}`}
                onClick={() => onStyleChange(s.uri)}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="wms-bulk-btns" style={{ marginTop: 8 }}>
            <button className="wms-bulk-btn" onClick={showAll}>{t('mapControls.showAll')}</button>
            <button className="wms-bulk-btn" onClick={hideAll}>{t('mapControls.hideAll')}</button>
          </div>

          {visibleGroups.map(group => {
            const isOpen = openGroups[group.id] !== false; // open by default
            const groupOnCount = group.layers.filter(isLayerOn).length;
            return (
              <div key={group.id} className="wms-group">
                <button
                  className="wms-group-header"
                  onClick={() => toggleGroup(group.id)}
                >
                  <span className="wms-group-label">{group.label}</span>
                  {groupOnCount > 0 && (
                    <span className="wms-group-count">{groupOnCount}</span>
                  )}
                  <span className="wms-group-chevron">{isOpen ? '▼' : '▶'}</span>
                </button>
                {isOpen && (
                  <div className="wms-group-items">
                    {group.layers.map(layer => {
                      const on = isLayerOn(layer);
                      return (
                        <div key={layer.id}
                          className={`layer-row ${on ? 'on' : 'off'}`}
                          title={layer.description || ''}
                          onClick={() => handleLayerToggle(layer)}>
                          <div className="lr-main">
                            <span className="lr-icon"
                              style={{ width: 8, height: 8, borderRadius: '50%',
                                       background: group.color, display: 'inline-block',
                                       flexShrink: 0 }} />
                            <div className="lr-info">
                              <span className="lr-name">{layer.label}</span>
                              <span className="lr-desc">{_shortDesc(layer.description)}</span>
                            </div>
                            <div className={`lr-toggle ${on ? 'active' : ''}`}>
                              <div className="lr-knob" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LayerControls;
